import Dexie, { type Table } from "dexie";
import type { Lemonade, Purchase, Sale } from "./types";

interface DBSale {
    id?: number;
    date: Date;
    amount: number;
    price: number;
    /**
     * FK → Lemonade.name
     */
    lemonadeName: string;
}

interface DBPurchase extends Purchase {
    id?: number;
}

class LemonadeDB extends Dexie {
    lemonades!: Table<Lemonade, string>;
    sales!: Table<DBSale, number>;
    purchases!: Table<DBPurchase, number>;

    constructor() {
        super("LemonadeDB");
        this.version(1).stores({
            lemonades: "name",
            sales: "++id, date, lemonadeName",
            purchases: "++id, date, product"
        });
    }

    /**
     * Adds a new lemonade to the database. The lemonade must not already exist.
     *
     * @param lemonade the lemonade to add.
     * @returns true if the lemonade was added, false if it already exists.
     */
    async addLemonade(lemonade: Lemonade): Promise<boolean> {
        // check if the lemonade already exists
        const existing = await this.lemonades.get(lemonade.name);
        if (existing) {
            return false;
        }

        this.lemonades.put(lemonade);
        return true;
    }

    /**
     * Gets a lemonade by its name.
     *
     * @param name the name of the lemonade to retrieve.
     * @returns the lemonade with the given name, or null if it does not exist.
     */
    async getLemonade(name: string): Promise<Lemonade | null> {
        return this.lemonades.get(name).then((lemonade) => {
            if (!lemonade) {
                return null;
            }
            return lemonade;
        });
    }

    /**
     * Removes a lemonade from the database.
     * 
     * @param lemonade the lemonade to remove.
     */
    async removeLemonade(lemonade: Lemonade): Promise<void> {
        await this.lemonades.delete(lemonade.name);
    }

    /**
     * Retrieves all lemonades from the database.
     */
    async getLemonades(): Promise<Lemonade[]> {
        return this.lemonades.toArray();
    }

    /**
     * Adds a sale to the database.
     * 
     * @param sale the sale to add.
     */
    async addSale(sale: Sale): Promise<void> {
        const dbSale: DBSale = {
            date: sale.date,
            amount: sale.amount,
            price: sale.price,
            lemonadeName: sale.lemonade.name
        };
        await this.sales.add(dbSale);
    }

    /**
     * Retrieves all sales from the database.
     * Each sale includes the associated lemonade object.
     * 
     * @returns an array of sales.
     */
    async getSales(): Promise<Sale[]> {
        const all = await this.sales.toArray();
        const result = await Promise.all(
            all.map(async (dbSale) => {
                const lemonade = await this.getLemonade(dbSale.lemonadeName);
                if (!lemonade) {
                    console.warn(`Limonade with name ${dbSale.lemonadeName} not found for sale`, dbSale);
                    return null;
                }
                return {
                    date: dbSale.date,
                    amount: dbSale.amount,
                    price: dbSale.price,
                    lemonade: lemonade
                } as Sale;
            })
        );
        // filter out null values
        return result.filter((x): x is NonNullable<typeof x> => x != null);
    }

    /**
     * Adds a purchase to the database.
     * 
     * @param purchase the purchase to add.
     */
    async addPurchase(purchase: Purchase): Promise<void> {
        await this.purchases.add(purchase);
    }

    /**
     * Retrieves all purchases from the database.
     * 
     * @returns an array of purchases.
     */
    async getPurchases(): Promise<Purchase[]> {
        return this.purchases.toArray();
    }
}
export const db = new LemonadeDB();
